import React, { Component } from "react";

import { LOGO_VERSION_REQUEST_STATUSES, BRAND_REQUEST_STATUSES } from "./enums";
import Utils from "./Utils";

/* eslint-disable no-unused-vars */
import BrandRequest from "./models/BrandRequest";
import LogoVersion from "./models/LogoVersion";
import LogoVersionRequest from "./models/LogoVersionRequest";
/* eslint-enable no-unused-vars */

import libraryIcon from "./libraryIcon.png";
import libraryIconGray from "./libraryIconGray.png";

import {
  table,
  tableBordered,
  tableHover,
  tablePrimary
} from "bootstrap/dist/css/bootstrap.css";

import {
  logoImageContainer,
  logoImage,
  libraryIcon as libraryIconClass,
  lgTable,
  subRow,
  brandCheckbox,
  actionColumn,
  logoversionCheckbox,
  urlText,
  badge as tableBadge,
  similarBrandsColumn
} from "./Table.css";
import BrandRequestBrand from "./models/BrandRequestBrand";
import Brand from "./models/Brand";

/**
 * @typedef {object} Props
 * @prop {BrandRequest[]=} brandRequests
 * @prop {((brandRequest: BrandRequest) => void)=} onRowClick
 * @prop {string=} selectedBrandRequestId
 * @prop {((logoVersionBase: LogoVersion | LogoVersionRequest, isChecked: boolean) => void)=} onLogoVersionCheck
 * @prop {((brandRequest: BrandRequest, isChecked: boolean) => void)=} onBrandRequestCheck
 * @prop {Object=} selectedBrandIndexes - key is BrandRequestIndex, value is BrandIndex
 * @prop {((brandRequest: number, brandIndex: number) => void)=} onSimilarBrandClick
 *
 * @typedef {object} State
 *
 * @extends {Component<Props, State>}
 */
export default class Table extends Component {
  constructor(props) {
    super(props);

    this.state = {
      /** brandRequestId vs checked */
      brandRequestsState: {},
      /** some logoVersions are checked */
      brandIndeterminate: {}
    };
  }

  /**
   *
   * @param {Props} nextProps
   */
  componentWillReceiveProps(nextProps) {
    const brandRequestsChanged =
      this.props.brandRequests !== nextProps.brandRequests;
    const selectedBrandIndexesChanged =
      this.props.selectedBrandIndexes !== nextProps.selectedBrandIndexes;

    if (brandRequestsChanged || selectedBrandIndexesChanged) {
      const brandRequestsState = {};
      const brandIndeterminate = {};
      const { selectedBrandIndexes } = nextProps;

      nextProps.brandRequests.forEach((brandRequest, brandRequestIndex) => {
        const selectedBrand =
          brandRequest.brands[selectedBrandIndexes[brandRequestIndex]];
        const brandLogoVersionIds = selectedBrand
          ? selectedBrand.logoVersions.map(logoVersion => logoVersion.id)
          : [];

        const tickedLogoVersionMap = brandRequest.logoVersionRequests.reduce(
          (acc, logoVersionRequest) => {
            if (
              logoVersionRequest.status ===
                LOGO_VERSION_REQUEST_STATUSES.CHECKED &&
              // Either an "existing in library" logoversion
              (brandLogoVersionIds.indexOf(logoVersionRequest.logoVersionId) !==
                -1 ||
                // Or freshly "uploaded" logoversion
                !logoVersionRequest.logoVersionId)
            ) {
              acc[
                logoVersionRequest.logoVersionId || logoVersionRequest.id
              ] = true;
            }
            return acc;
          },
          {}
        );

        const tickedCount = Object.keys(tickedLogoVersionMap).length;

        const logoVersionCount =
          brandLogoVersionIds.length +
          // Add LogoVersions uploaded by user
          brandRequest.logoVersionRequests.filter(
            lvr =>
              !lvr.logoVersionId &&
              selectedBrand !== undefined &&
              lvr.brandId == selectedBrand.id // eslint-disable-line eqeqeq
          ).length;

        brandRequestsState[brandRequest.id] =
          tickedCount > 0 && tickedCount === logoVersionCount;

        brandIndeterminate[brandRequest.id] =
          tickedCount > 0 && tickedCount !== logoVersionCount;
      });

      this.setState({
        brandRequestsState,
        brandIndeterminate
      });
    }
  }

  render() {
    const {
      brandRequests,
      selectedBrandRequestId,
      onLogoVersionCheck,
      onBrandRequestCheck,
      onRowClick,
      selectedBrandIndexes,
      onSimilarBrandClick
    } = this.props;
    const { brandRequestsState, brandIndeterminate } = this.state;

    return (
      <table className={`${lgTable} ${table} ${tableBordered} ${tableHover}`}>
        <thead>
          <tr>
            <th scope="col" style={{ width: "81px" }}>
              Thumb
            </th>
            <th scope="col" style={{ width: "50px" }}>
              {/* Octopus logo */}
            </th>
            <th scope="col">Brand Name</th>
            <th scope="col" style={{ width: "22%" }}>
              URL
            </th>
            <th scope="col" style={{ width: "195px" }}>
              Notes
            </th>
            <th scope="col">Select Brand</th>
            <th scope="col" style={{ width: "40px" }}>
              {/* Action/State */}
            </th>
          </tr>
        </thead>

        <tbody>
          {brandRequests.map((brandRequest, brandRequestIndex) => {
            const selectedBrandIndex = selectedBrandIndexes[brandRequestIndex];
            const selectedBrand = brandRequest.brands[selectedBrandIndex];
            const logoVersionRequests = brandRequest.logoVersionRequests.filter(
              lvr =>
                selectedBrand !== undefined && lvr.brandId == selectedBrand.id // eslint-disable-line eqeqeq
            );
            const logoVersions =
              (selectedBrand && selectedBrand.logoVersions) || [];
            // Select a preview thumb either from existing logo or uploaded logo if it's a new brand
            const previewUrl =
              (logoVersions.length && logoVersions[0].thumbUrl) ||
              (logoVersionRequests.length && logoVersionRequests[0].thumbUrl);

            const renderedLogoVersionList = logoVersions
              // Add LogoVersions that were uploaded by user
              .concat(
                brandRequest.logoVersionRequests.filter(
                  lvr =>
                    selectedBrand !== undefined &&
                    !lvr.logoVersionId &&
                    (!selectedBrand ||
                      // eqeqeq rule is broken for null == undefined to return true
                      // eslint-disable-next-line eqeqeq
                      (selectedBrand && lvr.brandId == selectedBrand.id))
                )
              );

            const isConfirmDisabled = Utils.isConfirmDisabled(
              brandRequest,
              selectedBrandIndex
            );
            const absoluteUrlRegex = new RegExp("^(?:[a-z]+:)?//", "i");
            const isAbsoluteUrl = absoluteUrlRegex.test(
              brandRequest.contentWebsite
            );

            return (
              <React.Fragment key={brandRequest.id}>
                <tr
                  onClick={() => onRowClick(brandRequest)}
                  className={
                    brandRequest.id === selectedBrandRequestId
                      ? tablePrimary
                      : ""
                  }
                >
                  {/* Thumb Url */}
                  <td>
                    <div
                      className={`${logoImage}`}
                      style={{
                        backgroundImage: `url('${previewUrl}')`
                      }}
                    />
                  </td>
                  {/* Octopus Icon */}
                  <td>
                    <img
                      src={
                        brandRequest.isInLibrary ? libraryIcon : libraryIconGray
                      }
                      className={`${libraryIconClass}`}
                      alt="logo"
                    />
                  </td>
                  {/* Brand Name */}
                  <td>{brandRequest.name}</td>
                  {/* Brand URL */}
                  <td className={`${urlText}`}>
                    <a
                      href={
                        (isAbsoluteUrl ? "" : "//") +
                        brandRequest.contentWebsite
                      }
                      target="_blank"
                    >
                      {brandRequest.contentWebsite}
                    </a>{" "}
                  </td>
                  {/* Notes */}
                  <td>{brandRequest.note}</td>
                  {/* Similar Brands */}
                  <td className={`${similarBrandsColumn}`}>
                    {[
                      new BrandRequestBrand({
                        brand: new Brand({ name: "New" })
                      }),
                      ...brandRequest.brandRequestBrands
                    ].map((brandRequestBrand, brandIndex) => {
                      const isSelected =
                        selectedBrandIndexes[brandRequestIndex] === brandIndex;

                      return (
                        <React.Fragment key={brandIndex}>
                          <a
                            href="#"
                            title={brandRequestBrand.brand.name}
                            onClick={event => {
                              event.preventDefault();
                              onSimilarBrandClick(
                                brandRequestIndex,
                                brandIndex
                              );
                            }}
                            style={{
                              backgroundColor:
                                brandRequestBrand.comparisonMethod ===
                                  "VisualSimilarity" && !isSelected
                                  ? "#D12555"
                                  : ""
                            }}
                            className={`badge badge-${
                              isSelected
                                ? "primary"
                                : brandRequestBrand.brand.id
                                ? "secondary"
                                : "success"
                            } ${tableBadge}`}
                          >
                            {brandRequestBrand.comparisonMethod ===
                              "VisualSimilarity" && (
                              <i
                                className="fa fa-exclamation-triangle mr-1"
                                aria-hidden="true"
                              ></i>
                            )}
                            {brandRequestBrand.brand.name}
                          </a>
                          {"\n"}
                        </React.Fragment>
                      );
                    })}
                    {/* Button trigger modal */}
                    <a
                      href="#"
                      title="Select brand from library..."
                      className={`badge badge-secondary ${tableBadge}`}
                      data-toggle="modal"
                      data-target="#lgLogoSearchModal"
                    >
                      ...
                    </a>
                  </td>
                  <td className={`${actionColumn}`}>
                    {/* Actions */}
                    <label
                      className={`custom-control custom-checkbox ${brandCheckbox}`}
                      onClick={e => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        className="custom-control-input"
                        checked={brandRequestsState[brandRequest.id]}
                        onChange={() =>
                          onBrandRequestCheck(
                            brandRequest,
                            !brandRequestsState[brandRequest.id]
                          )
                        }
                        onClick={e => e.stopPropagation()}
                        ref={elem =>
                          elem &&
                          (elem.indeterminate =
                            brandIndeterminate[brandRequest.id])
                        }
                        disabled={isConfirmDisabled}
                      />
                      <span
                        className={`custom-control-indicator ${brandRequestsState[
                          brandRequest.id
                        ] === false &&
                          brandIndeterminate[brandRequest.id] === false &&
                          brandRequest.status ===
                            BRAND_REQUEST_STATUSES.IGNORED &&
                          "fa fa-times"}`}
                      />
                    </label>
                  </td>
                </tr>
                {/* Sub-rows */}
                {brandRequest.id === selectedBrandRequestId &&
                  renderedLogoVersionList.length > 0 && (
                    <tr className={`${subRow}`}>
                      <td colSpan={7}>
                        {renderedLogoVersionList.map(logoVersion => {
                          // LogoVersions that were checked will have a record in LogoVersionRequests table with a status of -2
                          const logoVersionRequest = brandRequest.logoVersionRequests.find(
                            lvr =>
                              lvr.id === logoVersion.id ||
                              lvr.logoVersionId === logoVersion.id
                          );
                          const isChecked =
                            logoVersionRequest &&
                            logoVersionRequest.status ===
                              LOGO_VERSION_REQUEST_STATUSES.CHECKED;

                          return (
                            <div
                              key={logoVersion.id}
                              className={`${logoImageContainer}`}
                            >
                              {/* Sub-Row Logo Thumbnail */}
                              <div
                                className={`${logoImage}`}
                                style={{
                                  backgroundImage: `url('${logoVersion.thumbUrl}')`
                                }}
                              />
                              {/* Sub-Row Checkbox */}
                              <label
                                className={`custom-control custom-checkbox ${logoversionCheckbox}`}
                              >
                                <input
                                  type="checkbox"
                                  className="custom-control-input"
                                  checked={isChecked}
                                  onChange={() =>
                                    onLogoVersionCheck(logoVersion, !isChecked)
                                  }
                                  disabled={isConfirmDisabled}
                                />
                                <span className="custom-control-indicator" />
                              </label>
                            </div>
                          );
                        })}
                        {/* End of LogoVersions.map */}
                      </td>
                    </tr>
                  )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    );
  }
}

Table.defaultProps = {
  brandRequests: []
};



// WEBPACK FOOTER //
// ./src/Table.js